import React, { lazy, useState } from "react";
import FAQ from "../../pages/Faq";
import { useLocation, useNavigate } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import {
  FaBriefcase,
  FaUserCheck,
  FaCoins,
  FaFileSignature,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
  FaCalendarCheck,
  FaUserClock,
  FaClock,
} from "react-icons/fa6";

import {
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiMessageCircle,
  FiUsers,
  FiBriefcase,
} from "react-icons/fi";
import { Check } from "lucide-react";
import { FaShieldAlt } from "react-icons/fa";
import AppoinmentLetterGeneratorForm from "./AppoinmentLetterGeneratorForm";

const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Add the company name, address and authorised signatory information",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description: "Enter the employee’s name, address and contact details",
  },
  {
    number: 3,
    title: "Provide Appointment Details",
    description:
      "Add the job title, department, joining date, work location and reporting manager",
  },
  {
    number: 4,
    title: "Enter Salary Information",
    description:
      "Provide the annual CTC, monthly salary, incentives and applicable benefits",
  },
  {
    number: 5,
    title: "Define Employment Terms",
    description:
      "Enter the probation period, notice period, working schedule and leave entitlement",
  },
  {
    number: 6,
    title: "Generate the Letter",
    description:
      "The system will add the entered information to the standard Appointment Letter Template",
  },
  {
    number: 7,
    title: "Review and Issue",
    description:
      "Verify the employee’s name, salary, joining date and employment conditions before signing and sharing the letter",
  },
];
const features = [
  <>
    <strong>Startups:</strong> Create professional appointment letters while
    building clear and consistent HR processes
  </>,
  <>
    <strong>HR Teams:</strong> Prepare consistent appointment letters for
    different roles, departments, and employment types
  </>,
  <>
    <strong>Small and Medium-Sized Businesses:</strong> Create structured
    employee documents without preparing each letter manually
  </>,
  <>
    <strong>Business Owners:</strong> Issue professional appointment letters
    without needing a dedicated HR department
  </>,
  <>
    <strong>Recruitment and Staffing Companies:</strong> Prepare appointment
    letters for permanent, temporary, and contract employees
  </>,
  <>
    <strong>Consultants and CA Firms:</strong> Help clients create structured
    appointment letters and manage routine HR documentation
  </>,
];
const Faqs1 = [
  {
    title: "How Do I Create an Appointment Letter with Waggex?",
    content:
      "Enter your company and employee details, select the relevant job and employment information, add salary and other terms, then generate the letter. Waggex organizes the information into a professional appointment letter ready to download.",
  },
  {
    title: "What Information Do I Need to Generate the Letter?",
    content:
      "You’ll need basic company and employee information, including the employee’s name, designation, joining date, work location, salary, employment type, and applicable employment terms. The form guides you through the required details.",
  },
  {
    title:
      "Can I Create an Appointment Letter Without Editing a Word Template?",
    content:
      "Yes. Waggex removes the need to manually edit or format a Word template. Simply enter the required information in the form, and the generator places it into the appropriate sections of the appointment letter automatically.",
  },
  {
    title: "Can I Include Salary, Probation and Notice Period Details?",
    content:
      "Yes. You can add key employment terms such as salary, benefits, probation, working conditions, leave, notice period, responsibilities, and company policies so the generated appointment letter contains the relevant details.",
  },
  {
    title: "Can I Download the Appointment Letter as a PDF?",
    content:
      "Yes. Once you complete the required fields, Waggex generates the appointment letter in a professionally structured PDF format. You can download the file and use it for sharing, printing, or signing.",
  },
  {
    title: "Who Can Use the Waggex Appointment Letter Generator?",
    content:
      "The generator can be used by business owners, HR teams, startups, small businesses, recruitment agencies, and consultants who need to create professional appointment letters without preparing each document manually.",
  },
];
const schema = [
  // =========================================================
  // SCHEMA 1: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is an appointment letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An Appointment Letter is a formal document issued by an employer to confirm an employee's appointment, role, salary, joining date, and important employment conditions. It serves as the official start of the employment relationship.",
        },
      },
      {
        "@type": "Question",
        name: "What details should an appointment letter contain?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A standard appointment letter should include designation, department, joining date, work location, employment type, salary structure, probation period, notice period, working hours, leave entitlement, and references to company policies.",
        },
      },
      {
        "@type": "Question",
        name: "Can I create an appointment letter online?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The Waggex Appointment Letter Generator allows you to create a professionally structured appointment letter online by entering details and downloading the result as a formatted PDF instantly.",
        },
      },
      {
        "@type": "Question",
        name: "Can I create an appointment letter PDF?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The Waggex Appointment Letter Generator creates and downloads the letter as a PDF once all required fields are completed, ready to send electronically or print for signature.",
        },
      },
      {
        "@type": "Question",
        name: "When should the appointment letter be issued?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An appointment letter should be issued at or before the employee's joining date after the offer letter is accepted. It should be signed by both the employer's authorised signatory and the employee on or before the first working day.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 2: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];

const appoinmentLetterContent = [
  {
    icon: FaUserCheck,
    title: "Appointment Confirmation",
    description: "Confirm the employee’s appointment and selected position.",
  },
  {
    icon: FaBriefcase,
    title: "Job Details",
    description:
      "Add the designation, department, reporting manager, and employment type.",
  },
  {
    icon: FaCalendarCheck,
    title: "Joining Information",
    description: "Enter the joining date, work location, and mode of work.",
  },
  {
    icon: FaCoins,
    title: "Salary and Benefits",
    description:
      "Add the CTC, monthly salary, incentives, allowances, and benefits.",
  },
  {
    icon: FaUserClock,
    title: "Probation and Confirmation",
    description: "Specify the probation period and confirmation process.",
  },
  {
    icon: FaClock,
    title: "Working Conditions",
    description:
      "Set out working days, hours, weekly offs, and attendance expectations.",
  },
  {
    icon: FaFileSignature,
    title: "Leave and Notice Period",
    description:
      "Include leave entitlement and applicable notice period requirements.",
  },
  {
    icon: FaShieldAlt,
    title: "Confidentiality and Policies",
    description:
      "Set clear expectations for confidentiality, workplace conduct, and company property.",
  },
];
const offerLetterPoints = [
  "Job title and department",
  "Employment type",
  "Date of joining",
  "Salary and benefits",
  "Probation period",
  "Offer acceptance deadline",
];

const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Avoid drafting every appointment Letter manually or copying details from older documents",
  },
  {
    id: 2,
    icon: <FiFileText />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent appointment letter template for different roles, departments and employment types",
  },
  {
    id: 3,
    icon: <FiAlertTriangle />,
    title: "Reduce Manual Errors",
    desc: "Structured form fields help reduce incorrect names, salary figures, dates and job information",
  },
  {
    id: 4,
    icon: <FiMessageCircle />,
    title: "Improve Candidate Communication",
    desc: "Clearly explain the position, compensation, joining date and important employment terms",
  },
  {
    id: 5,
    icon: <FiUsers />,
    title: "Support Faster Hiring",
    desc: "Create and issue the Employee appointment Letter soon after the candidate is selected",
  },
  {
    id: 6,
    icon: <FiBriefcase />,
    title: "Suitable for Different Businesses",
    desc: "The format can be used by startups, SMEs, recruiters, consultants, HR teams and established organisations",
  },
];

const offerPoints = [
  "Proposed designation",
  "Salary and benefits",
  "Work location",
  "Joining date",
  "Basic employment terms",
];

const appointmentPoints = [
  "Probation",
  "Responsibilities",
  "Leave & Attendance",
  "Notice period",
  "Termination",
  "Company policies",
];
export default function AppointmentLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Free Online Appointment Letter Generator | Waggex</title>
        <meta
          name="description"
          content="Create a professional appointment letter online with Waggex. Enter employee, job, salary and terms to generate and download your letter instantly"
        />
        <meta
          name="keywords"
          content="appointment letter generator online,appointment letter format India,free appointment letter maker online,professional appointment letter template India,appointment letter PDF generator India,job appointment letter format India,employee appointment letter India,appointment letter Word format India,online appointment letter creator India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/appointment-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Create Professional Appointment Letter Online For Free
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create a professional appointment letter in minutes. Add employee,
              job, salary and joining details, then download a ready-to-use PDF
              for your new hire
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <AppoinmentLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS AN APPOINTMENT LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      What’s Included in an Appointment Letter?
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      An appointment letter in India generally includes the
                      employee’s job title, joining date, salary, work location,
                      reporting details, and key employment terms. It gives both
                      the employer and employee a clear record of the agreed
                      conditions before the employee starts work
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {offerLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < offerLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  What Does the Offer Letter Template Include?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Create a Complete Appointment Letter
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center sm:max-w-3xl lg:max-w-6xl mx-auto">
                  Add all the key employment details in the tool, and this
                  appointment generator creates a professional appointment
                  letter ready to be shared. Include the employee’s role,
                  joining details, salary, working conditions, and company
                  policies, then download the completed letter as PDF in one
                  click
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {appoinmentLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE AN APPOINTMENT LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Generate Your Appointment Letter Fast
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 7).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 2 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}
                  {/* <div className="mb-6 flex items-center gap-4">
                    <span className="h-px w-11 bg-[#10324F]" />

                    <span className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                      Understand the Difference
                    </span>
                  </div> */}
                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      Understand the Difference
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    What Is The Difference Between Appointment Letter vs Offer
                    Letter
                  </h2>

                  {/* Intro */}
                  <p className="mt-7 max-w-2xl text-sm leading-8 text-[#10324F]">
                    An offer letter and an appointment letter are both part of
                    the hiring process, but they serve different purposes. An
                    offer letter outlines the proposed terms of employment,
                    while an appointment letter formally confirms the employee’s
                    appointment and provides more detailed employment terms
                  </p>

                  {/* Explanation points */}
                  <div className="mt-9 space-y-7">
                    <div className="flex items-start gap-2">
                      <FaCaretRight className="mt-1.5 shrink-0 text-2xl text-[#10324F]" />

                      <p className="text-sm leading-8 text-[#10324F]">
                        An <strong> Offer letter</strong> is normally issued
                        after a candidate is selected but before joining
                      </p>
                    </div>

                    <div className="flex items-start gap-2">
                      <FaCaretRight className="mt-1.5 shrink-0 text-2xl text-[#10324F]" />

                      <p className="text-sm leading-8 text-[#10324F]">
                        A Job <strong> Appointment Letter</strong> formally
                        confirms the employee’s appointment and may include more
                        detailed terms
                      </p>
                    </div>
                  </div>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-5 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Offer Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-5 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {offerPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-5 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Appointment Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-70 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-5 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {appointmentPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  Can I Use This Online Appointment Letter?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Professional Employee Documents for Every Industry
                </h2>
                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Create structured appointment letters for new employees with
                  company, employee, job, salary, benefits and employment terms
                  in one place
                </p>
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS APPOINTMENT LETTER FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide mb-4">
                  Suitable for Employers and HR Professionals
                </h2>
                {/* 
                <p className="font-roboto text-[#10324F] text-center text-sm md:text-base font-medium leading-relaxed py-4 sm:max-w-3xl  md:max-w-5xl mx-auto mb-4">
                  We help businesses across India manage GPS-based attendance
                  for office teams, remote employees, field staff, and
                  multi-location workforces. HR and admin teams get real-time
                  location-verified attendance records before payroll processing
                </p> */}
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-2  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm sm:text-lg font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Your Appointment Letter Online
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Enter the employee, salary and employment details to prepare a
                clear and professionally structured Appointment Letter
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Appointment Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
